import { useEffect, useState, useCallback, useRef } from 'react'
import { useMaskito } from '@maskito/react'
import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import DialogContent from '@mui/material/DialogContent'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import CircularProgress from '@mui/material/CircularProgress'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'

import {
  useAddParkingListMutation,
  useGetParkingListQuery,
  useLazyGetParkingUsersQuery,
  useParkingListUpdateMutation
} from './api-slice'
import { phoneMaskParentheses } from '@/utils/phone-masks'


// DialogForm.propTypes = {
//   buildingId: PropTypes.string.isRequired,
//   currentId: PropTypes.number,
//   onClose: PropTypes.func,
//   open: PropTypes.bool
// }
const initialForm = {
  number: '',
  phone: '',
  flat: '',
  status: false,
  search: '',
  user: null,
  car_number: ''
}

const DialogForm = ({
  open = false,
  onClose = () => {},
  currentId = null,
  buildingId 
}) => {
  const { t } = useTranslation()
  const [updateList, { isSuccess: updateSuccess, isLoading: updateLoading }] =
    useParkingListUpdateMutation()
  const [createParking, { isSuccess: addedSuccess, isLoading }] =
    useAddParkingListMutation()
  const { data } = useGetParkingListQuery({ buildingId }, { skip: !buildingId })
  const list = data?.data?.find((item) => item.id === currentId)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [form, setForm] = useState(initialForm)
  const phoneInputRef = useMaskito({ options: phoneMaskParentheses })
  const userSearchRequestTimeoutRef = useRef(null);
  // const [openComplete, setOpenComplete] = useState(false)

  // const {
  //   data: users,
  //   isSuccess,
  //   isLoading: usersLoading
  // } = useGetUsersQuery({ search: form.search }, { skip: !form.search })
  const [getUsers, { data: users, isSuccess, isLoading: usersLoading }] =
    useLazyGetParkingUsersQuery(undefined)
  // const [options, setOptions] = useState([])

  const handleChange = ({ target }) => {
    setForm({
      ...form,
      [target.name]: target.value
    })
  }

  
  const handleSnackOpen = () => {
    setOpenSnackbar(true)
  }
  
  const handleSearch = useCallback(({ target }) => {
    setForm({ ...form, search: target.value })
    
    // try {
      //   await getUsers({ search: target.value })
      // } catch (err) {
        //   console.log(err)
        // }
  }, [form])
    
  const onInputChange = (e) => {
    if (userSearchRequestTimeoutRef.current) {
      clearTimeout(userSearchRequestTimeoutRef.current);
    }
    // Start a new timer for the delay
    userSearchRequestTimeoutRef.current = setTimeout(() => {
      handleSearch(e)
    }, 300);
  }

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return
    setOpenSnackbar(false)
  }

  const onSuccess = () => {
    setForm(initialForm)
    onClose()
    handleSnackOpen()
  }

  const handleClose = () => {
    setForm(initialForm)
    onClose()
  }

  const handleSubmit = async () => {
    if (currentId) {
      try {
        await updateList({
          id: currentId,
          building_id: +buildingId,
          number: form.number,
          phone: `+${form.phone.replace(/[^\d]/g, '')}`,
          flat: String(form.flat),
          is_active: form.status === 1,
          user_id: form.user || null,
          car_number: form.car_number // Добавляем отправку номера авто
        }).unwrap()
        onSuccess()
      } catch (err) {
        console.log(err)
      }
    } else {
      try {
        await createParking({
          building_id: +buildingId,
          number: form.number,
          phone: `+${form.phone.replace(/[^\d]/g, '')}`,
          flat: String(form.flat),
          is_active: form.status === 1,
          user_id: form.user || null,
          car_number: form.car_number // Добавляем отправку номера авто
        }).unwrap()
        onSuccess()
      } catch (err) {
        console.log(err)
      }
    }
  }

  useEffect(() => {
    if (currentId && list) {
      setForm({
        number: list.number,
        phone: list.phone,
        flat: list.flat,
        status: list.is_active ? 1 : 2,
        user: list.user_id,
        car_number: list.car_number || '' // Загружаем номер авто
      })
    }
  }, [currentId, list])

  useEffect(() => () => {
    if (userSearchRequestTimeoutRef.current) {
      clearTimeout(userSearchRequestTimeoutRef.current);
    }
  }, [])

  const handleUsers = useCallback(async () => {
    try {
      await getUsers({ search: form.search }).unwrap()
    } catch (err) {
      console.log(err)
    }
  }, [form.search, getUsers])

  useEffect(() => {
    if (form.search) {
      handleUsers()
    }
  }, [form.search, handleUsers])

  // useEffect(() => {
  //   if (!openComplete) {
  //     setOptions([])
  //   }
  // }, [openComplete])

  // useEffect(() => {
  //   if (isSuccess) {
  //     setOptions(users)
  //   }
  // }, [isSuccess, users])

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth>
        {(isLoading || updateLoading) && <LinearProgress />}
        <DialogTitle>
          {currentId ? t('editing') : t('add_a_parking_space')}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FormControl fullWidth sx={{ mr: 1 }}>
              <InputLabel>{t('parking_spaces_text_add_number')}</InputLabel>
              <OutlinedInput
                name="number"
                value={form.number}
                onChange={handleChange}
                type="number"
                label={t('parking_spaces_text_add_number')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('parking_spaces_text_add_phone')}</InputLabel>
              <OutlinedInput
                inputRef={phoneInputRef}
                name="phone"
                type="tel"
                id="phone-mask"
                label={t('parking_spaces_text_add_phone')}
                value={form.phone}
                onInput={handleChange}
              />
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel>{t('select_status')}</InputLabel>
              <Select
                labelId="status"
                id="status"
                value={form.status || ''}
                label={t('select_status')}
                name="status"
                onChange={handleChange}
              >
                <MenuItem value={1}>{t('active')}</MenuItem>
                <MenuItem value={2}>{t('inactive')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel>{t('parking_spaces_text_flat_number')}</InputLabel>
              <OutlinedInput
                name="flat"
                value={form.flat}
                onChange={handleChange}
                type="number"
                label={t('parking_spaces_text_flat_number')}
              />
            </FormControl>
            {/* <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel>{t('house_number')}</InputLabel>
              <OutlinedInput
                name="building"
                value={form.building}
                onChange={handleChange}
                label={t('house_number')}
              />
            </FormControl> */}
          </Box>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <Autocomplete
              id="user"
              // open={openComplete}
              // onOpen={() => {
              //   setOpenComplete(true)
              // }}
              // onClose={() => {
              //   setOpenComplete(false)
              // }}
              autoComplete
              includeInputInList
              filterSelectedOptions
              options={users || []}
              loading={usersLoading}
              onInputChange={onInputChange}
              onFocus={onInputChange}
              onChange={(e, newValue) =>
                setForm({ ...form, user: newValue.id })
              }
              lang="ru"
              selectOnFocus
              freeSolo
              getOptionLabel={(option) =>
                `${option.first_name} ${
                  option?.last_name ? option.last_name : ''
                }`
              }
              isOptionEqualToValue={(option, value) =>
                option.phone_number === value.phone_number
              }
              // sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="search"
                  id="search"
                  label={t('user')}
                  value={form.user}
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps?.input,
                      endAdornment: (
                        <>
                          {usersLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.slotProps?.input?.endAdornment}
                        </>
                      )
                    }
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>{t('car_number')}</InputLabel>
            <OutlinedInput
              name="car_number"
              value={form.car_number || ''}
              onChange={handleChange}
              label={t('car_number')}
            />
          </FormControl>
          <Button
            sx={{ mt: 3 }}
            fullWidth
            variant="contained"
            // disabled={!validateForm}
            onClick={handleSubmit}
          >
            {buildingId ? t('save') : t('add')}
          </Button>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackClose}
      >
        <Alert
          sx={{ width: '100%' }}
          severity="success"
          variant="filled"
          onClose={handleSnackClose}
        >
          {addedSuccess
            ? t('the_number_was_successfully_created!')
            : updateSuccess && t('saved')}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DialogForm
